import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { 
  Building, 
  MapPin, 
  Cpu, 
  Users, 
  Scale, 
  ClipboardList, 
  Handshake, 
  Plus, 
  Trash2, 
  AlertTriangle,
  Check,
  Star,
  Bell
} from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader, AlertBanner } from "../../../components/ui";
import { customerService, extractBillingAddressFlag, extractAssetQuantity, toBooleanFlag } from "../../../services/customerService";
import { CRM_ONBOARDING_LIST_PATH } from "../routes";
import { SITE_TYPE_OPTIONS, ASSET_STATUS_OPTIONS, WARRANTY_TYPE_OPTIONS, AMC_TYPE_OPTIONS } from "../constants";

const NO_AMC_TYPE = "No AMC";

function toAmcTypeDisplay(rawValue) {
  // Backend returns the enum form (e.g. "No_AMC", "Non_Comprehensive"); the UI
  // dropdown uses the display label with spaces (e.g. "No AMC").
  return String(rawValue || "").trim().replace(/_/g, " ");
}

function isNoAmcType(asset = {}) {
  const raw = asset.amcType || asset.amc_type || asset.AMCType || asset.amctype || "";
  return toAmcTypeDisplay(raw) === NO_AMC_TYPE;
}

function formatContactTypeLabel(typeRaw) {
  if (!typeRaw) return "Secondary";
  const str = String(typeRaw).trim().toLowerCase();
  if (str === "primary") return "Primary";
  if (str === "secondary") return "Secondary";
  if (str === "technical") return "Technical";
  if (str === "emergency") return "Emergency";
  if (str === "general") return "General";
  return "Secondary";
}


function translateSiteTypeLabel(type) {
  const map = {
    Commercial: "crm.siteTypeCommercial",
    Industrial: "crm.siteTypeIndustrial",
    Residential: "crm.siteTypeResidential",
    Warehouse: "inventory.warehouse",
    Remote: "crm.siteTypeRemote"
  };
  return map[type] ? i18n.t(map[type]) : type;
}

function translateAmcTypeLabel(type) {
  const map = {
    Comprehensive: "crm.comprehensive",
    "Non Comprehensive": "crm.nonComprehensive",
    "No AMC": "crm.noAmc"
  };
  return map[type] ? i18n.t(map[type]) : type;
}

function translateWarrantyTypeLabel(type) {
  if (type === "Yes") return i18n.t("common.yes");
  if (type === "No") return i18n.t("common.no");
  return type;
}

function translateAssetStatusLabel(status) {
  if (status === "Active") return i18n.t("status.active");
  if (status === "Inactive") return i18n.t("status.inactive");
  return status;
}

function translateContactTypeDisplay(type) {
  const map = {
    Primary: "crm.primary",
    Secondary: "crm.secondary",
    Technical: "crm.technical",
    Emergency: "crm.emergency",
    General: "common.general"
  };
  return map[type] ? i18n.t(map[type]) : type;
}


/** Map API accountType to create/update option values (underscore form). */
function mapAccountTypeForEditForm(typeRaw) {
  const str = String(typeRaw || "Normal").trim();
  if (str === "Top_Account" || str === "Top Account") return "Top_Account";
  if (str === "Hyper_Care" || str === "Hyper Care") return "Hyper_Care";
  return "Normal";
}

/** Map API accountStatus to create option values (Title_Case / On_Hold). */
function mapAccountStatusForEditForm(statusRaw) {
  const str = String(statusRaw || "Onboarding")
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, "_");

  if (str === "active") return "Active";
  if (str === "on_hold" || str === "onhold" || str.includes("hold")) return "On_Hold";
  if (str === "inactive") return "Inactive";
  return "Onboarding";
}

function emptyTaxLegal() {
  return {
    legalName: "",
    entityType: "",
    gstin: "",
    pan: "",
    gstRegistrationType: "",
    isTdsDeductor: false
  };
}

function mapTaxFromApi(tax = {}) {
  return {
    legalName: tax.legalName || tax.legal_name || "",
    entityType: tax.entityType || tax.entity_type || "",
    gstin: tax.gstin || "",
    pan: tax.pan || "",
    gstRegistrationType: tax.gstRegistrationType || tax.gst_registration_type || "",
    isTdsDeductor: Boolean(tax.isTdsDeductor ?? tax.is_tds_deductor)
  };
}

function emptyCommercialTerms() {
  return {
    paymentTerms: "",
    customDays: "",
    creditLimit: "",
    retentionPercentage: "",
    retentionRelease: "",
    securityDepositAmount: "",
    billingCycle: "",
    preferredPaymentMode: "",
    rateCardTier: ""
  };
}

function mapCommercialFromApi(terms = {}) {
  const numOrEmpty = (v) => (v === undefined || v === null || v === "" ? "" : String(v));
  return {
    paymentTerms: terms.paymentTerms || terms.payment_terms || "",
    customDays: numOrEmpty(
      terms.paymentTermsCustomDays ??
        terms.payment_terms_custom_days ??
        terms.customDays ??
        terms.custom_days
    ),
    creditLimit: numOrEmpty(terms.creditLimit ?? terms.credit_limit),
    retentionPercentage: numOrEmpty(terms.retentionPercentage ?? terms.retention_percentage),
    retentionRelease: terms.retentionRelease || terms.retention_release || "",
    securityDepositAmount: numOrEmpty(terms.securityDepositAmount ?? terms.security_deposit_amount),
    billingCycle: terms.billingCycle || terms.billing_cycle || "",
    preferredPaymentMode: terms.preferredPaymentMode || terms.preferred_payment_mode || "",
    rateCardTier: terms.rateCardTier || terms.rate_card_tier || ""
  };
}

function emptyRelationship() {
  return {
    leadSource: "",
    creditRiskRating: "",
    creditHold: false,
    preferredContactChannel: "",
    accountOwnerId: ""
  };
}

function mapRelationshipFromApi(rel = {}) {
  const ownerId = rel.accountOwnerId ?? rel.account_owner_id;
  return {
    leadSource: rel.leadSource || rel.lead_source || "",
    creditRiskRating: rel.creditRiskRating || rel.credit_risk_rating || "",
    creditHold: Boolean(rel.creditHold ?? rel.credit_hold),
    preferredContactChannel: rel.preferredContactChannel || rel.preferred_contact_channel || "",
    accountOwnerId: ownerId === undefined || ownerId === null || ownerId === "" ? "" : String(ownerId)
  };
}

function emptyLocation() {
  return {
    locationName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    siteType: "",
    isActive: true,
    isBillingAddress: false
  };
}

function mapLocationFromApi(loc = {}) {
  const billing = extractBillingAddressFlag(loc, false);
  return {
    ...loc,
    id: loc.id || loc.locationId || loc.location_id,
    locationId: loc.locationId || loc.location_id || loc.id,
    locationName: loc.locationName || loc.location_name || "",
    addressLine1: loc.addressLine1 || loc.address_line1 || "",
    addressLine2: loc.addressLine2 || loc.address_line2 || "",
    city: loc.city || "",
    state: loc.state || "",
    siteType: loc.siteType || loc.site_type || "",
    isActive: toBooleanFlag(loc.isActive ?? loc.is_active, true),
    isBillingAddress: billing,
    is_billing_address: billing,
    billingAddress: billing,
    billing_address: billing
  };
}

function toDateInputValue(value) {
  if (value === undefined || value === null || value === "") return "";
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function emptyAsset(defaultLocationId = "") {
  return {
    locationId: defaultLocationId,
    assetName: "",
    assetCategory: "",
    manufacturer: "",
    modelNumber: "",
    serialNumber: "",
    installDate: "",
    warrantyType: "",
    warrantyStartDate: "",
    warrantyExpiry: "",
    amcType: "",
    amcOrder: "",
    amcOrderDate: "",
    tenantAssetId: "",
    AMCStartDate: "",
    AMCEndDate: "",
    assetStatus: "Active",
    quantity: 1
  };
}

function mapAssetFromApi(asset = {}) {
  const warrantyExpiry = toDateInputValue(
    asset.warrantyExpiry ||
      asset.warranty_expiry ||
      asset.warrantyEndDate ||
      asset.warranty_end_date
  );
  const amcStart = toDateInputValue(
    asset.AMCStartDate ||
      asset.amcStartDate ||
      asset.amc_start_date ||
      asset.AMC_Start_Date ||
      asset.amcstartDate
  );
  const amcEnd = toDateInputValue(
    asset.AMCEndDate ||
      asset.amcEndDate ||
      asset.amc_end_date ||
      asset.AMC_End_Date ||
      asset.amcendDate
  );
  const amcOrderDate = toDateInputValue(
    asset.amcOrderDate ||
      asset.amc_order_date ||
      asset.AMCOrderDate ||
      asset.AMC_Order_Date ||
      asset.amcorderDate
  );

  return {
    ...asset,
    id: asset.id || asset.assetId || asset.asset_id,
    assetId: asset.assetId || asset.asset_id || asset.id,
    locationId: asset.locationId || asset.location_id || "",
    assetName: asset.assetName || asset.asset_name || "",
    assetCategory: asset.assetCategory || asset.asset_category || "",
    manufacturer: asset.manufacturer || "",
    modelNumber: asset.modelNumber || asset.model_number || "",
    serialNumber: asset.serialNumber || asset.serial_number || "",
    installDate: toDateInputValue(asset.installDate || asset.install_date),
    warrantyType: asset.warrantyType || asset.warranty_type || "",
    warrantyStartDate: toDateInputValue(asset.warrantyStartDate || asset.warranty_start_date),
    warrantyExpiry,
    warrantyEndDate: warrantyExpiry,
    amcType: toAmcTypeDisplay(asset.amcType || asset.amc_type || asset.AMCType || asset.amctype || ""),
    amcOrder: asset.amcOrder || asset.amc_order || asset.AMCOrder || asset.amcOrderNo || asset.amc_order_no || asset.amcorderNo || "",
    amcOrderDate,
    tenantAssetId: asset.tenantAssetId || asset.tenant_asset_id || asset.TenantAssetId || "",
    AMCStartDate: amcStart,
    AMCEndDate: amcEnd,
    amcStartDate: amcStart,
    amcEndDate: amcEnd,
    assetStatus: (() => {
      const raw = asset.assetStatus || asset.asset_status || "Active";
      if (String(raw).toLowerCase() === "inactive" || raw === "InActive") return "Inactive";
      return raw;
    })(),
    quantity: extractAssetQuantity(asset) ?? 1
  };
}

export function CustomerOnboardingPage() {
  const navigate = useNavigate();
  const { customerId: routeCustomerId } = useParams();
  const [searchParams] = useSearchParams();
  const editCustomerId = routeCustomerId || searchParams.get("edit");
  const isEditMode = Boolean(editCustomerId);

  // Wizard state
  const [onboardingStep, setOnboardingStep] = useState("basic-info");
  const [formWarning, setFormWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmittingStep, setIsSubmittingStep] = useState(false);
  const [toast, setToast] = useState(null);
  const [createdCustomerId, setCreatedCustomerId] = useState(editCustomerId);
  const [contactCount, setContactCount] = useState(0);
  const [locationCount, setLocationCount] = useState(0);
  const [assetCount, setAssetCount] = useState(0);
  const [stateOptions, setStateOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [entityTypeOptions, setEntityTypeOptions] = useState([]);
  const [gstRegistrationTypeOptions, setGstRegistrationTypeOptions] = useState([]);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [retentionReleaseOptions, setRetentionReleaseOptions] = useState([]);
  const [billingCycleOptions, setBillingCycleOptions] = useState([]);
  const [preferredPaymentModeOptions, setPreferredPaymentModeOptions] = useState([]);
  const [rateCardTierOptions, setRateCardTierOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  const [creditRiskRatingOptions, setCreditRiskRatingOptions] = useState([]);
  const [preferredContactChannelOptions, setPreferredContactChannelOptions] = useState([]);
  const [accountTypeOptions, setAccountTypeOptions] = useState([]);
  const [accountStatusOptions, setAccountStatusOptions] = useState([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [lookupsError, setLookupsError] = useState(null);

  const activeCustomerId = createdCustomerId || editCustomerId;

  useEffect(() => {
    if (editCustomerId) {
      setCreatedCustomerId(editCustomerId);
    }
  }, [editCustomerId]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLookupsLoading(true);
      setLookupsError(null);
      try {
        const [states, categories, industryTypes, taxOpts, commercialOpts, relationshipOpts, customerInfoOpts] =
          await Promise.all([
            customerService.getOrgStates(),
            customerService.getAssetCategoryOptions(0, 10),
            customerService.getIndustryTypeOptions(),
            customerService.getTaxInfoDropdownOptions(),
            customerService.getCommercialTermsDropdownOptions(),
            customerService.getRelationshipDropdownOptions(),
            customerService.getCustomerInfoDropdownOptions()
          ]);
        if (!active) return;
        setStateOptions(states || []);
        setCategoryOptions(categories || []);
        setIndustryTypeOptions(industryTypes || []);
        setEntityTypeOptions(taxOpts?.entityType || []);
        setGstRegistrationTypeOptions(taxOpts?.gstRegistrationType || []);
        setPaymentTermsOptions(commercialOpts?.paymentTerms || []);
        setRetentionReleaseOptions(commercialOpts?.retentionRelease || []);
        setBillingCycleOptions(commercialOpts?.billingCycle || []);
        setPreferredPaymentModeOptions(commercialOpts?.preferredPaymentMode || []);
        setRateCardTierOptions(commercialOpts?.rateCardTier || []);
        setLeadSourceOptions(relationshipOpts?.leadSource || []);
        setCreditRiskRatingOptions(relationshipOpts?.creditRiskRating || []);
        setPreferredContactChannelOptions(relationshipOpts?.preferredContactChannel || []);
        setAccountTypeOptions(customerInfoOpts?.accountType || []);
        setAccountStatusOptions(customerInfoOpts?.accountStatus || []);
      } catch (err) {
        console.error("Failed to load CRM lookup options:", err);
        if (active) setLookupsError(err?.message || i18n.t("crm.failedToLoadLookupOptions"));
      } finally {
        if (active) setLookupsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Form Fields State
  const [basicInfo, setBasicInfo] = useState({
    companyName: "",
    industryType: "",
    accountType: "Normal",
    accountStatus: "Onboarding",
    accountOwner: "",
    accountOwnerId: ""
  });

  const [taxLegal, setTaxLegal] = useState(emptyTaxLegal());
  const [hasTaxInfoRecord, setHasTaxInfoRecord] = useState(false);

  const [commercialTerms, setCommercialTerms] = useState(emptyCommercialTerms());
  const [hasCommercialTermsRecord, setHasCommercialTermsRecord] = useState(false);

  const [relationship, setRelationship] = useState(emptyRelationship());
  const [hasRelationshipRecord, setHasRelationshipRecord] = useState(false);

  const [contacts, setContacts] = useState([
    { contactType: "Primary", firstName: "", lastName: "", email: "", phone: "", jobTitle: "", isPrimary: true, receivesNotifications: true }
  ]);

  const [locations, setLocations] = useState([emptyLocation()]);

  const [assets, setAssets] = useState([emptyAsset()]);

  // Fetch Tax Info by Customer from API (single record)
  const loadTaxInfoForCustomer = useCallback(async (custId) => {
    if (!custId) return null;
    try {
      const tax = await customerService.getTaxInfoByCustomerId(custId);
      if (tax) {
        setTaxLegal(mapTaxFromApi(tax));
        setHasTaxInfoRecord(true);
        return tax;
      }
      setTaxLegal(emptyTaxLegal());
      setHasTaxInfoRecord(false);
      return null;
    } catch (err) {
      console.error("Failed to load customer tax info from API:", err);
      setHasTaxInfoRecord(false);
      return null;
    }
  }, []);

  // Fetch Commercial Terms by Customer from API (single record)
  const loadCommercialTermsForCustomer = useCallback(async (custId) => {
    if (!custId) return null;
    try {
      const terms = await customerService.getCommercialTermsByCustomerId(custId);
      if (terms) {
        setCommercialTerms(mapCommercialFromApi(terms));
        setHasCommercialTermsRecord(true);
        return terms;
      }
      setCommercialTerms(emptyCommercialTerms());
      setHasCommercialTermsRecord(false);
      return null;
    } catch (err) {
      console.error("Failed to load customer commercial terms from API:", err);
      setHasCommercialTermsRecord(false);
      return null;
    }
  }, []);

  // Fetch Relationship by Customer from API (single record)
  const loadRelationshipForCustomer = useCallback(async (custId) => {
    if (!custId) return null;
    try {
      const rel = await customerService.getRelationshipByCustomerId(custId);
      if (rel) {
        setRelationship(mapRelationshipFromApi(rel));
        setHasRelationshipRecord(true);
        return rel;
      }
      setRelationship(emptyRelationship());
      setHasRelationshipRecord(false);
      return null;
    } catch (err) {
      console.error("Failed to load customer relationship from API:", err);
      setHasRelationshipRecord(false);
      return null;
    }
  }, []);

  // Fetch Contacts by Customer from API
  const loadContactsForCustomer = useCallback(async (custId) => {
    if (!custId) return;
    try {
      const res = await customerService.getContactsByCustomer(custId, 0, 100);
      const list = res?.contacts || [];
      if (list.length > 0) {
        const mapped = list.map((c) => ({
          id: c.id || c.contactId || c.contact_id,
          contactId: c.contactId || c.contact_id || c.id,
          contactType: formatContactTypeLabel(c.contactType || c.contact_type),
          firstName: c.firstName || c.first_name || "",
          lastName: c.lastName || c.last_name || "",
          email: c.email || "",
          phone: c.phone || "",
          jobTitle: c.jobTitle || c.job_title || "",
          isPrimary: Boolean(c.isPrimary ?? c.is_primary),
          receivesNotifications: Boolean(c.receivesNotifications ?? c.receives_notifications)
        }));
        setContacts(mapped);
      }
      const count = await customerService.getContactCountByCustomer(custId);
      setContactCount(count || list.length);
    } catch (err) {
      console.error("Failed to load customer contacts from API:", err);
    }
  }, []);

  // Fetch Locations by Customer from API
  const loadLocationsForCustomer = useCallback(async (custId) => {
    if (!custId) return [];
    try {
      const res = await customerService.getLocationsByCustomer(custId, 0, 100, "locationId", "ASC");
      const list = res?.locations || [];
      // Enrich from by-id so billing/active flags are present even if search DTO omits them.
      const mapped = await Promise.all(
        list.map(async (loc) => {
          const locationId = loc.id || loc.locationId || loc.location_id;
          if (!locationId) return mapLocationFromApi(loc);
          try {
            const detail = await customerService.getLocationById(locationId);
            return mapLocationFromApi({ ...loc, ...(detail || {}) });
          } catch {
            return mapLocationFromApi(loc);
          }
        })
      );

      if (mapped.length > 0) {
        setLocations(mapped);
      } else {
        setLocations([emptyLocation()]);
      }
      const count = await customerService.getLocationCountByCustomer(custId);
      setLocationCount(count || mapped.length);
      return mapped;
    } catch (err) {
      console.error("Failed to load customer locations from API:", err);
      return [];
    }
  }, []);

  // Fetch Assets by Customer from API
  const loadAssetsForCustomer = useCallback(async (custId, locationList = []) => {
    if (!custId) return;
    try {
      const res = await customerService.getAssetsByCustomer(custId, 0, 100, "assetId", "ASC");
      const list = Array.isArray(res?.assets) ? res.assets : [];
      const defaultLocId = (locationList || []).find((l) => l.locationId || l.id)?.locationId
        || (locationList || []).find((l) => l.locationId || l.id)?.id
        || "";

      const mapped = await Promise.all(
        list.map(async (asset) => {
          const assetId = asset.id || asset.assetId || asset.asset_id;
          if (!assetId) return mapAssetFromApi(asset);
          try {
            const detail = await customerService.getAssetById(assetId);
            return mapAssetFromApi({ ...asset, ...(detail || {}) });
          } catch {
            return mapAssetFromApi(asset);
          }
        })
      );

      if (mapped.length > 0) {
        setAssets(mapped);
      } else {
        setAssets([emptyAsset(defaultLocId)]);
      }
      try {
        const count = await customerService.getAssetCountByCustomer(custId);
        setAssetCount(count || mapped.length);
      } catch {
        setAssetCount(mapped.length);
      }
    } catch (err) {
      console.error("Failed to load customer assets from API:", err);
      setFormWarning({ message: err?.message || i18n.t("crm.failedToLoadCustomerAssets") });
    }
  }, []);

  // Fetch customer details in edit mode
  useEffect(() => {
    if (isEditMode && editCustomerId) {
      setLoading(true);
      customerService.getCustomerById(editCustomerId).then(async (cust) => {
        if (cust) {
          const ownerId = cust.accountOwnerId ?? cust.account_owner_id;

          setBasicInfo({
            companyName: cust.companyName || cust.company_name || "",
            industryType: cust.industryType || cust.industry_type || "",
            accountType: mapAccountTypeForEditForm(cust.accountType || cust.account_type),
            accountStatus: mapAccountStatusForEditForm(cust.accountStatus || cust.account_status),
            accountOwner: cust.accountOwner || cust.account_owner || "",
            accountOwnerId: ownerId === undefined || ownerId === null || ownerId === "" ? "" : String(ownerId)
          });

          await loadTaxInfoForCustomer(editCustomerId);
          await loadCommercialTermsForCustomer(editCustomerId);
          await loadRelationshipForCustomer(editCustomerId);
          await loadContactsForCustomer(editCustomerId);
          const locList = await loadLocationsForCustomer(editCustomerId);
          await loadAssetsForCustomer(editCustomerId, locList);
        }
      }).catch(err => {
        console.error("Failed to load customer details", err);
        setFormWarning({ message: i18n.t("crm.failedToLoadCustomerProfileFromServer") });
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [editCustomerId, isEditMode, loadTaxInfoForCustomer, loadCommercialTermsForCustomer, loadRelationshipForCustomer, loadContactsForCustomer, loadLocationsForCustomer, loadAssetsForCustomer]);

  useEffect(() => {
    if (activeCustomerId && onboardingStep === "tax-legal") {
      loadTaxInfoForCustomer(activeCustomerId);
    }
  }, [activeCustomerId, onboardingStep, loadTaxInfoForCustomer]);

  useEffect(() => {
    if (activeCustomerId && onboardingStep === "commercial-terms") {
      loadCommercialTermsForCustomer(activeCustomerId);
    }
  }, [activeCustomerId, onboardingStep, loadCommercialTermsForCustomer]);

  useEffect(() => {
    if (activeCustomerId && onboardingStep === "relationship") {
      loadRelationshipForCustomer(activeCustomerId);
    }
  }, [activeCustomerId, onboardingStep, loadRelationshipForCustomer]);

  useEffect(() => {
    if (activeCustomerId && onboardingStep === "contacts") {
      loadContactsForCustomer(activeCustomerId);
    }
  }, [activeCustomerId, onboardingStep, loadContactsForCustomer]);

  useEffect(() => {
    if (activeCustomerId && onboardingStep === "locations") {
      loadLocationsForCustomer(activeCustomerId);
    }
  }, [activeCustomerId, onboardingStep, loadLocationsForCustomer]);

  useEffect(() => {
    if (activeCustomerId && onboardingStep === "assets") {
      (async () => {
        const locList = await loadLocationsForCustomer(activeCustomerId);
        await loadAssetsForCustomer(activeCustomerId, locList);
      })();
    }
  }, [activeCustomerId, onboardingStep, loadLocationsForCustomer, loadAssetsForCustomer]);

  // Repeatable Contacts Handlers
  const addContact = () => {
    setContacts([
      ...contacts,
      { contactType: "Secondary", firstName: "", lastName: "", email: "", phone: "", jobTitle: "", isPrimary: false, receivesNotifications: true }
    ]);
  };

  const updateContact = (idx, field, val) => {
    const updated = [...contacts];
    updated[idx][field] = val;
    setContacts(updated);
  };

  const removeContact = (idx) => {
    setContacts(contacts.filter((_, i) => i !== idx));
  };

  // Toggle Primary Contact API Call
  const handleTogglePrimary = async (idx, contact) => {
    const contactId = contact.id || contact.contactId;
    const newIsPrimary = !contact.isPrimary;
    updateContact(idx, "isPrimary", newIsPrimary);

    if (contactId) {
      try {
        await customerService.updatePrimaryContact(contactId, newIsPrimary);
        if (activeCustomerId) loadContactsForCustomer(activeCustomerId);
      } catch (err) {
        console.error("Failed to toggle primary status:", err);
      }
    }
  };

  // Toggle Notification Preference API Call
  const handleToggleNotifications = async (idx, contact) => {
    const contactId = contact.id || contact.contactId;
    const newNotif = !contact.receivesNotifications;
    updateContact(idx, "receivesNotifications", newNotif);

    if (contactId) {
      try {
        await customerService.updateNotificationPreference(contactId, newNotif);
        if (activeCustomerId) loadContactsForCustomer(activeCustomerId);
      } catch (err) {
        console.error("Failed to toggle notification preference:", err);
      }
    }
  };

  // Repeatable Locations Handlers
  const addLocation = () => {
    setLocations([...locations, emptyLocation()]);
  };

  const updateLocation = (idx, field, val) => {
    setLocations((prev) =>
      prev.map((loc, i) => {
        if (i !== idx) return loc;
        const next = { ...loc, [field]: val };
        if (field === "isBillingAddress") {
          next.is_billing_address = val;
          next.billingAddress = val;
          next.billing_address = val;
        }
        if (field === "isActive") {
          next.is_active = val;
        }
        return next;
      })
    );
  };

  const removeLocation = async (idx) => {
    const loc = locations[idx];
    const locationId = loc?.id || loc?.locationId || loc?.location_id;

    if (locationId) {
      try {
        await customerService.deleteLocation(locationId);
        if (activeCustomerId) {
          await loadLocationsForCustomer(activeCustomerId);
        } else {
          const remaining = locations.filter((_, i) => i !== idx);
          setLocations(remaining.length > 0 ? remaining : [emptyLocation()]);
          setLocationCount(Math.max(0, locationCount - 1));
        }
        setToast({ message: i18n.t("crm.locationDeletedSuccessfully"), type: "success" });
        setTimeout(() => setToast(null), 2000);
      } catch (err) {
        console.error("Failed to delete location:", err);
        setFormWarning({ message: err?.message || i18n.t("crm.failedToDeleteLocation") });
      }
      return;
    }

    const remaining = locations.filter((_, i) => i !== idx);
    setLocations(remaining.length > 0 ? remaining : [emptyLocation()]);
  };

  const handleToggleLocationActive = async (idx, loc) => {
    const locationId = loc.id || loc.locationId || loc.location_id;
    const nextActive = !(loc.isActive ?? loc.is_active ?? true);
    updateLocation(idx, "isActive", nextActive);

    if (locationId) {
      try {
        await customerService.toggleLocationStatus(locationId, nextActive);
        if (activeCustomerId) loadLocationsForCustomer(activeCustomerId);
      } catch (err) {
        console.error("Failed to toggle location status:", err);
        updateLocation(idx, "isActive", !nextActive);
        setFormWarning({ message: err?.message || i18n.t("crm.failedToUpdateLocationStatus") });
      }
    }
  };

  // Repeatable Assets Handlers
  const getDefaultLocationId = () => {
    const first = locations.find((l) => l.locationId || l.id);
    return first?.locationId || first?.id || "";
  };

  const addAsset = () => {
    setAssets([...assets, emptyAsset(getDefaultLocationId())]);
  };

  const updateAsset = (idx, field, val) => {
    const updated = [...assets];
    updated[idx][field] = val;
    setAssets(updated);
  };

  const handleAmcTypeChange = (idx, nextType) => {
    setAssets((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const next = { ...row, amcType: nextType, amc_type: nextType, AMCType: nextType };
        if (nextType === NO_AMC_TYPE) {
          next.amcOrder = "";
          next.amc_order = "";
          next.AMCOrder = "";
          next.amcOrderNo = "";
          next.amc_order_no = "";
          next.amcOrderDate = "";
          next.amc_order_date = "";
          next.AMCOrderDate = "";
          next.AMCStartDate = "";
          next.amcStartDate = "";
          next.amc_start_date = "";
          next.AMC_Start_Date = "";
          next.AMCEndDate = "";
          next.amcEndDate = "";
          next.amc_end_date = "";
          next.AMC_End_Date = "";
        }
        return next;
      })
    );
  };

  const removeAsset = async (idx) => {
    const asset = assets[idx];
    const assetId = asset?.id || asset?.assetId || asset?.asset_id;

    if (assetId) {
      try {
        await customerService.deleteAsset(assetId);
        if (activeCustomerId) {
          await loadAssetsForCustomer(activeCustomerId, locations);
        } else {
          const remaining = assets.filter((_, i) => i !== idx);
          setAssets(remaining.length > 0 ? remaining : [emptyAsset(getDefaultLocationId())]);
          setAssetCount(Math.max(0, assetCount - 1));
        }
        setToast({ message: i18n.t("crm.assetDeletedSuccessfully"), type: "success" });
        setTimeout(() => setToast(null), 2000);
      } catch (err) {
        console.error("Failed to delete asset:", err);
        setFormWarning({ message: err?.message || i18n.t("crm.failedToDeleteAsset") });
      }
      return;
    }

    const remaining = assets.filter((_, i) => i !== idx);
    setAssets(remaining.length > 0 ? remaining : [emptyAsset(getDefaultLocationId())]);
  };

  const handleAssetStatusChange = async (idx, asset, newStatus) => {
    updateAsset(idx, "assetStatus", newStatus);
    const assetId = asset.id || asset.assetId || asset.asset_id;
    if (!assetId || !newStatus.trim()) return;

    try {
      await customerService.updateAssetStatus(assetId, newStatus.trim());
      if (activeCustomerId) await loadAssetsForCustomer(activeCustomerId, locations);
    } catch (err) {
      console.error("Failed to update asset status:", err);
      setFormWarning({ message: err?.message || i18n.t("crm.failedToUpdateAssetStatus") });
    }
  };

  const handleRelocateAsset = async (idx, asset, newLocationId) => {
    updateAsset(idx, "locationId", newLocationId);
    const assetId = asset.id || asset.assetId || asset.asset_id;
    if (!assetId || !newLocationId) return;

    try {
      await customerService.relocateAsset(assetId, newLocationId);
      if (activeCustomerId) await loadAssetsForCustomer(activeCustomerId, locations);
      setToast({ message: i18n.t("crm.assetRelocatedSuccessfully"), type: "success" });
      setTimeout(() => setToast(null), 2000);
    } catch (err) {
      console.error("Failed to relocate asset:", err);
      setFormWarning({ message: err?.message || i18n.t("crm.failedToRelocateAsset") });
    }
  };

  const navigateBack = () => {
    navigate(CRM_ONBOARDING_LIST_PATH);
  };

  // Step 1: Customer Information Save Handler
  const handleSaveCustomerInfo = async () => {
    if (isSubmittingStep) return;
    if (!basicInfo.companyName?.trim()) {
      setFormWarning({ message: i18n.t("crm.companyNameIsRequired") });
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    const payload = {
      companyName: basicInfo.companyName,
      industryType: basicInfo.industryType,
      accountType: basicInfo.accountType,
      accountStatus: basicInfo.accountStatus,
      accountOwner: basicInfo.accountOwner,
      accountOwnerId: Number(basicInfo.accountOwnerId) || 1
    };

    try {
      let savedCust;
      if (isEditMode && editCustomerId) {
        savedCust = await customerService.updateCustomer(editCustomerId, payload);
        setToast({ message: i18n.t("crm.customerInformationUpdatedMovingToTaxLegal"), type: "success" });
      } else {
        savedCust = await customerService.createCustomer(payload);
        const newId = savedCust?.id || savedCust?.customerId || savedCust?.customer_id;
        if (newId) {
          setCreatedCustomerId(newId);
        }
        setToast({ message: i18n.t("crm.customerCreatedSuccessfullyMovingToTaxLegal"), type: "success" });
      }

      setTimeout(() => {
        setToast(null);
        setLoading(false);
        setIsSubmittingStep(false);
        setOnboardingStep("tax-legal");
      }, 700);
    } catch (err) {
      console.error("Save customer info failed:", err);
      const errMsg = err?.data?.accountStatus || err?.message || i18n.t("crm.failedToSaveCustomerRecord");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Step 2: Tax & Legal Save Handler (single record per customer)
  const handleSaveTaxLegal = async () => {
    if (isSubmittingStep) return;
    const custId = activeCustomerId;
    if (!custId) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveCustomerInformationFirstToGenerateACustomerId") });
      setOnboardingStep("basic-info");
      return;
    }

    const gstin = (taxLegal.gstin || "").trim();
    const pan = (taxLegal.pan || "").trim();
    if (gstin && gstin.length !== 15) {
      setFormWarning({ message: i18n.t("crm.gstinMustBeExactly15Characters") });
      return;
    }
    if (pan && pan.length !== 10) {
      setFormWarning({ message: i18n.t("crm.panMustBeExactly10Characters") });
      return;
    }

    const hasAnyTaxField =
      Boolean((taxLegal.legalName || "").trim()) ||
      Boolean((taxLegal.entityType || "").trim()) ||
      Boolean(gstin) ||
      Boolean(pan) ||
      Boolean((taxLegal.gstRegistrationType || "").trim()) ||
      Boolean(taxLegal.isTdsDeductor);

    // Allow advancing without API call when nothing entered and no existing record
    if (!hasAnyTaxField && !hasTaxInfoRecord) {
      setFormWarning(null);
      setOnboardingStep("commercial-terms");
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    const payload = {
      customerId: Number(custId),
      legalName: taxLegal.legalName || "",
      entityType: taxLegal.entityType || "",
      gstin,
      pan,
      gstRegistrationType: taxLegal.gstRegistrationType || "",
      isTdsDeductor: Boolean(taxLegal.isTdsDeductor)
    };

    try {
      if (hasTaxInfoRecord) {
        await customerService.updateTaxInfo(custId, payload);
      } else {
        await customerService.createTaxInfo(payload);
        setHasTaxInfoRecord(true);
      }

      await loadTaxInfoForCustomer(custId);

      if (isEditMode) {
        setToast({ message: i18n.t("crm.taxInformationUpdated"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
        }, 700);
      } else {
        setToast({ message: i18n.t("crm.taxInformationSavedMovingToCommercialTerms"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
          setOnboardingStep("commercial-terms");
        }, 700);
      }
    } catch (err) {
      console.error("Save tax info failed:", err);
      const errMsg =
        err?.data?.gstin ||
        err?.data?.pan ||
        err?.data?.legalName ||
        err?.data?.entityType ||
        err?.message ||
        i18n.t("crm.failedToSaveTaxInformation");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Step 3: Commercial Terms Save Handler (single record per customer)
  const handleSaveCommercialTerms = async () => {
    if (isSubmittingStep) return;
    const custId = activeCustomerId;
    if (!custId) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveCustomerInformationFirstToGenerateACustomerId") });
      setOnboardingStep("basic-info");
      return;
    }

    if (commercialTerms.paymentTerms === "Custom") {
      const days = Number(commercialTerms.customDays);
      if (!commercialTerms.customDays || !Number.isFinite(days) || days <= 0) {
        setFormWarning({ message: i18n.t("crm.pleaseEnterValidCustomCreditDaysForCustomPaymentTerms") });
        return;
      }
    }

    const hasAnyField =
      Boolean((commercialTerms.paymentTerms || "").trim()) ||
      Boolean((commercialTerms.customDays || "").toString().trim()) ||
      Boolean((commercialTerms.creditLimit || "").toString().trim()) ||
      Boolean((commercialTerms.retentionPercentage || "").toString().trim()) ||
      Boolean((commercialTerms.retentionRelease || "").trim()) ||
      Boolean((commercialTerms.securityDepositAmount || "").toString().trim()) ||
      Boolean((commercialTerms.billingCycle || "").trim()) ||
      Boolean((commercialTerms.preferredPaymentMode || "").trim()) ||
      Boolean((commercialTerms.rateCardTier || "").trim());

    if (!hasAnyField && !hasCommercialTermsRecord) {
      setFormWarning(null);
      setOnboardingStep("relationship");
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    const payload = {
      customerId: Number(custId),
      paymentTerms: commercialTerms.paymentTerms || "",
      paymentTermsCustomDays: commercialTerms.customDays,
      creditLimit: commercialTerms.creditLimit,
      retentionPercentage: commercialTerms.retentionPercentage,
      retentionRelease: commercialTerms.retentionRelease || "",
      securityDepositAmount: commercialTerms.securityDepositAmount,
      billingCycle: commercialTerms.billingCycle || "",
      preferredPaymentMode: commercialTerms.preferredPaymentMode || "",
      rateCardTier: commercialTerms.rateCardTier || ""
    };

    try {
      if (hasCommercialTermsRecord) {
        await customerService.updateCommercialTerms(custId, payload);
      } else {
        await customerService.createCommercialTerms(payload);
        setHasCommercialTermsRecord(true);
      }

      await loadCommercialTermsForCustomer(custId);

      if (isEditMode) {
        setToast({ message: i18n.t("crm.commercialTermsUpdated"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
        }, 700);
      } else {
        setToast({ message: i18n.t("crm.commercialTermsSavedMovingToRelationship"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
          setOnboardingStep("relationship");
        }, 700);
      }
    } catch (err) {
      console.error("Save commercial terms failed:", err);
      const errMsg =
        err?.data?.paymentTerms ||
        err?.data?.creditLimit ||
        err?.data?.billingCycle ||
        err?.message ||
        i18n.t("crm.failedToSaveCommercialTerms");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Step 4: Relationship Save Handler (single record per customer)
  const handleSaveRelationship = async () => {
    if (isSubmittingStep) return;
    const custId = activeCustomerId;
    if (!custId) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveCustomerInformationFirstToGenerateACustomerId") });
      setOnboardingStep("basic-info");
      return;
    }

    const hasAnyField =
      Boolean((relationship.leadSource || "").trim()) ||
      Boolean((relationship.creditRiskRating || "").trim()) ||
      Boolean((relationship.preferredContactChannel || "").trim()) ||
      Boolean((relationship.accountOwnerId || "").toString().trim()) ||
      Boolean(relationship.creditHold);

    if (!hasAnyField && !hasRelationshipRecord) {
      setFormWarning(null);
      setOnboardingStep("contacts");
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    const payload = {
      customerId: Number(custId),
      leadSource: relationship.leadSource || "",
      creditRiskRating: relationship.creditRiskRating || "",
      creditHold: Boolean(relationship.creditHold),
      preferredContactChannel: relationship.preferredContactChannel || "",
      accountOwnerId: relationship.accountOwnerId
    };

    try {
      if (hasRelationshipRecord) {
        await customerService.updateRelationship(custId, payload);
      } else {
        await customerService.createRelationship(payload);
        setHasRelationshipRecord(true);
      }

      await loadRelationshipForCustomer(custId);

      if (isEditMode) {
        setToast({ message: i18n.t("crm.relationshipUpdated"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
        }, 700);
      } else {
        setToast({ message: i18n.t("crm.relationshipSavedMovingToContacts"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
          setOnboardingStep("contacts");
        }, 700);
      }
    } catch (err) {
      console.error("Save relationship failed:", err);
      const errMsg =
        err?.data?.leadSource ||
        err?.data?.creditRiskRating ||
        err?.data?.preferredContactChannel ||
        err?.message ||
        i18n.t("crm.failedToSaveRelationship");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Step: Contacts Save Handler (Single Batch Invocation)
  const handleSaveContacts = async () => {
    if (isSubmittingStep) return;
    const custId = activeCustomerId;
    if (!custId) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveCustomerInformationFirstToGenerateACustomerId") });
      setOnboardingStep("basic-info");
      return;
    }

    const validContacts = contacts.filter(c => (c.firstName || c.first_name || "").trim() || (c.email || "").trim());
    if (validContacts.length === 0) {
      setOnboardingStep("locations");
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    try {
      await Promise.all(
        validContacts.map((c) => {
          const contactId = c.id || c.contactId;
          const payload = {
            customerId: Number(custId),
            firstName: c.firstName || c.first_name || "",
            lastName: c.lastName || c.last_name || "",
            email: c.email || "",
            phone: c.phone || "",
            jobTitle: c.jobTitle || c.job_title || "",
            contactType: formatContactTypeLabel(c.contactType || c.contact_type),
            isPrimary: Boolean(c.isPrimary ?? c.is_primary),
            receivesNotifications: Boolean(c.receivesNotifications ?? c.receives_notifications)
          };

          if (contactId) {
            return customerService.updateContact(contactId, payload);
          }
          return customerService.createContact(payload);
        })
      );

      await loadContactsForCustomer(custId);
      setToast({ message: i18n.t("crm.contactsSavedSuccessfully"), type: "success" });
      setTimeout(() => {
        setToast(null);
        setLoading(false);
        setIsSubmittingStep(false);
        setOnboardingStep("locations");
      }, 700);
    } catch (err) {
      console.error("Save contacts failed:", err);
      const errMsg = err?.data?.contactType || err?.message || i18n.t("crm.failedToSaveContacts");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Step 3: Locations Save Handler (supports multiple locations per customer)
  const handleSaveLocations = async () => {
    if (isSubmittingStep) return;
    const custId = activeCustomerId;
    if (!custId) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveCustomerInformationFirstToGenerateACustomerId") });
      setOnboardingStep("basic-info");
      return;
    }

    const validLocations = locations.filter(
      (l) => (l.locationName || l.location_name || "").trim() || (l.addressLine1 || l.address_line1 || "").trim() || (l.city || "").trim()
    );

    if (validLocations.length === 0) {
      setOnboardingStep("assets");
      return;
    }

    const incomplete = validLocations.find(
      (l) => !(l.locationName || l.location_name || "").trim() || !(l.addressLine1 || l.address_line1 || "").trim() || !(l.city || "").trim()
    );
    if (incomplete) {
      setFormWarning({ message: i18n.t("crm.eachLocationRequiresLocationNameAddressLine1AndCity") });
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    try {
      await Promise.all(
        validLocations.map((l) => {
          const locationId = l.id || l.locationId || l.location_id;
          const payload = {
            customerId: Number(custId),
            locationName: l.locationName || l.location_name || "",
            addressLine1: l.addressLine1 || l.address_line1 || "",
            addressLine2: l.addressLine2 || l.address_line2 || "",
            city: l.city || "",
            state: l.state || "",
            siteType: l.siteType || l.site_type || "",
            isActive: toBooleanFlag(l.isActive ?? l.is_active, true),
            isBillingAddress: extractBillingAddressFlag(l, false)
          };

          if (locationId) {
            return customerService.updateLocation(locationId, payload);
          }
          return customerService.createLocation(payload);
        })
      );

      // Reload latest persisted values (including isBillingAddress) after save.
      await loadLocationsForCustomer(custId);
      setToast({ message: i18n.t("crm.locationsSavedSuccessfully"), type: "success" });
      setTimeout(() => {
        setToast(null);
        setLoading(false);
        setIsSubmittingStep(false);
        setOnboardingStep("assets");
      }, 700);
    } catch (err) {
      console.error("Save locations failed:", err);
      const errMsg = err?.data?.locationName || err?.message || i18n.t("crm.failedToSaveLocations");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Step 4: Assets Save Handler (supports multiple assets per customer/location)
  const handleSaveAssets = async () => {
    if (isSubmittingStep) return;
    const custId = activeCustomerId;
    if (!custId) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveCustomerInformationFirstToGenerateACustomerId") });
      setOnboardingStep("basic-info");
      return;
    }

    const savedLocations = locations.filter((l) => l.locationId || l.id);
    if (savedLocations.length === 0) {
      setFormWarning({ message: i18n.t("crm.pleaseSaveAtLeastOneLocationBeforeAddingAssets") });
      setOnboardingStep("locations");
      return;
    }

    const validAssets = assets.filter(
      (a) => (a.assetName || a.asset_name || "").trim() || (a.assetCategory || a.asset_category || "").trim()
    );

    if (validAssets.length === 0) {
      setToast({ message: i18n.t("crm.customerOnboardingCompletedSuccessfully"), type: "success" });
      setTimeout(() => {
        setToast(null);
        navigateBack();
      }, 800);
      return;
    }

    const incomplete = validAssets.find((a) => {
      const name = (a.assetName || a.asset_name || "").trim();
      const category = (a.assetCategory || a.asset_category || "").trim();
      const locationId = a.locationId || a.location_id;
      return !name || !category || !locationId;
    });
    if (incomplete) {
      setFormWarning({ message: i18n.t("crm.eachAssetRequiresLocationAssetNameAndCategory") });
      return;
    }

    setIsSubmittingStep(true);
    setLoading(true);
    setFormWarning(null);

    try {
      await Promise.all(
        validAssets.map(async (a) => {
          const assetId = a.id || a.assetId || a.asset_id;
          const payload = {
            customerId: Number(custId),
            locationId: Number(a.locationId || a.location_id),
            assetName: a.assetName || a.asset_name || "",
            assetCategory: a.assetCategory || a.asset_category || "",
            manufacturer: a.manufacturer || "",
            modelNumber: a.modelNumber || a.model_number || "",
            serialNumber: a.serialNumber || a.serial_number || "",
            installDate: a.installDate || a.install_date || "",
            warrantyType: a.warrantyType || a.warranty_type || "",
            warrantyStartDate: a.warrantyStartDate || a.warranty_start_date || "",
            warrantyExpiry: a.warrantyExpiry || a.warranty_expiry || a.warrantyEndDate || a.warranty_end_date || "",
            warrantyEndDate: a.warrantyExpiry || a.warranty_expiry || a.warrantyEndDate || a.warranty_end_date || "",
            // amcType: a.amcType || a.amc_type || a.AMCType || "",
            amcType:a.amcType ||a.amc_type ||a.AMCType ||a.amctype ||"",
            // amcOrder: isNoAmcType(a) ? "" : (a.amcOrder || a.amc_order || a.AMCOrder || a.amcOrderNo || a.amc_order_no || ""),
            amcOrder: isNoAmcType(a) ? "": (a.amcOrder ||a.amc_order ||a.AMCOrder ||a.amcOrderNo ||a.amc_order_no ||a.amcorderNo ||""),
            // amcOrderDate: isNoAmcType(a) ? "" : (a.amcOrderDate || a.amc_order_date || a.AMCOrderDate || ""),
            amcOrderDate: isNoAmcType(a)? "": (a.amcOrderDate ||a.amc_order_date ||a.AMCOrderDate ||a.amcorderDate ||""),
            tenantAssetId: a.tenantAssetId || a.tenant_asset_id || a.TenantAssetId || "",
            AMCStartDate: isNoAmcType(a) ? "" : (a.AMCStartDate || a.amcStartDate || a.amc_start_date || a.AMC_Start_Date || ""),
            AMCEndDate: isNoAmcType(a) ? "" : (a.AMCEndDate || a.amcEndDate || a.amc_end_date || a.AMC_End_Date || ""),
            assetStatus: a.assetStatus || a.asset_status || "Active",
            quantity: extractAssetQuantity(a) ?? (Number(a.quantity) || 1)
          };

          if (assetId) {
            return customerService.updateAsset(assetId, payload);
          }
          return customerService.createAsset(payload);
        })
      );

      // Always reload from search API so Edit reopen shows persisted assets
      const locList = await loadLocationsForCustomer(custId);
      await loadAssetsForCustomer(custId, locList);

      if (isEditMode) {
        setToast({ message: i18n.t("crm.assetsSavedSuccessfully"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
        }, 700);
      } else {
        setToast({ message: i18n.t("crm.assetsSavedSuccessfullyOnboardingComplete"), type: "success" });
        setTimeout(() => {
          setToast(null);
          setLoading(false);
          setIsSubmittingStep(false);
          navigateBack();
        }, 800);
      }
    } catch (err) {
      console.error("Save assets failed:", err);
      const errMsg = err?.data?.assetName || err?.data?.locationId || err?.message || i18n.t("crm.failedToSaveAssets");
      setFormWarning({ message: String(errMsg) });
      setLoading(false);
      setIsSubmittingStep(false);
    }
  };

  // Generic Save Handler for Wizard
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (isSubmittingStep) return;

    if (onboardingStep === "basic-info") {
      await handleSaveCustomerInfo();
      return;
    }

    if (onboardingStep === "tax-legal") {
      await handleSaveTaxLegal();
      return;
    }

    if (onboardingStep === "commercial-terms") {
      await handleSaveCommercialTerms();
      return;
    }

    if (onboardingStep === "relationship") {
      await handleSaveRelationship();
      return;
    }

    if (onboardingStep === "contacts") {
      await handleSaveContacts();
      return;
    }

    if (onboardingStep === "locations") {
      await handleSaveLocations();
      return;
    }

    if (onboardingStep === "assets") {
      await handleSaveAssets();
      return;
    }

    const steps = ["basic-info", "tax-legal", "commercial-terms", "relationship", "contacts", "locations", "assets"];
    const idx = steps.indexOf(onboardingStep);
    if (idx >= 0 && idx < steps.length - 1) {
      setOnboardingStep(steps[idx + 1]);
    }
  };

  return (
    <AppLayout
      overlay={
        toast ? (
          <div className="fixed bottom-5 right-5 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300 pointer-events-none">
            <div className="flex items-center gap-2.5 bg-white border border-[var(--color-text-border)]/60 text-foreground px-4 py-3 rounded-2xl shadow-[0_15px_40px_-10px_rgba(30,51,131,0.25)] font-sans text-sm font-medium">
              <div className="h-6 w-6 rounded-full bg-[var(--color-success)]/10 text-[var(--color-success)] grid place-items-center">
                <Check className="h-4 w-4" />
              </div>
              <span>{toast.message}</span>
            </div>
          </div>
        ) : null
      }
    >
      <div className="max-w-6xl mx-auto space-y-3 pb-10">
        <div className="flex flex-col gap-2">
          <PageBackLink to={CRM_ONBOARDING_LIST_PATH} />

          <PageHeader
            className="mb-0"
            title={isEditMode ? `${i18n.t("crm.editProfile")} ${basicInfo.companyName || i18n.t("common.customer")}` : i18n.t("crm.onboardNewCustomer")}
            // subtitle="Register billing data, primary accounts, active warehouses, and installed machinery registers."
          />
        </div>

        {formWarning && (
          <AlertBanner type="error">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{formWarning.message}</span>
            </div>
          </AlertBanner>
        )}

        <div className="sticky top-0 z-20 bg-[var(--color-workspace)] pb-2">
          <div className="flex flex-wrap gap-2 border-b border-[var(--color-text-border)]/50 pb-2">
            {[
              { id: "basic-info", label: i18n.t("crm.information"), icon: Building },
              { id: "tax-legal", label: i18n.t("crm.taxAndLegal"), icon: Scale },
              { id: "commercial-terms", label: i18n.t("crm.commercialTerms"), icon: ClipboardList },
              { id: "relationship", label: i18n.t("crm.relationship"), icon: Handshake },
              { id: "contacts", label: `${i18n.t("crm.contacts")} (${contactCount})`, icon: Users },
              { id: "locations", label: `${i18n.t("crm.locations")} (${locationCount})`, icon: MapPin },
              { id: "assets", label: `${i18n.t("crm.assets")} (${assetCount})`, icon: Cpu }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = onboardingStep === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setFormWarning(null);
                    setOnboardingStep(tab.id);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
                    isActive
                      ? "bg-[var(--color-primary)] text-white shadow-[0_4px_12px_rgba(30,51,131,0.2)]"
                      : "hover:bg-[var(--color-workspace)] text-[var(--color-text-secondary)] hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSave} className="relative z-0 bg-white rounded-2xl border border-white/60 p-6 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] space-y-6">
          
          {/* STEP 1: BASIC INFORMATION */}
          {onboardingStep === "basic-info" && (
            <div className="space-y-5">
              <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.basicInformation" /></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.companyName" /></label>
                  <input
                    type="text"
                    required
                    placeholder={i18n.t("examples.eGSterlingRoboticsLtd")}
                    value={basicInfo.companyName}
                    onChange={(e) => setBasicInfo({ ...basicInfo, companyName: e.target.value })}
                    className="h-10 w-full rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.industrySector" /></label>
                  <select
                    value={basicInfo.industryType}
                    onChange={(e) => setBasicInfo({ ...basicInfo, industryType: e.target.value })}
                    disabled={lookupsLoading && industryTypeOptions.length === 0}
                    className="h-10 w-full rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && industryTypeOptions.length === 0
                        ? i18n.t("crm.loadingIndustrySectors")
                        : industryTypeOptions.length === 0
                          ? i18n.t("crm.noIndustrySectorsAvailable")
                          : i18n.t("crm.selectIndustrySector")}
                    </option>
                    {industryTypeOptions.map((opt) => (
                      <option key={`${opt.id || opt.value}-${opt.label}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    {basicInfo.industryType &&
                      !industryTypeOptions.some((o) => o.value === basicInfo.industryType) && (
                        <option value={basicInfo.industryType}>{basicInfo.industryType}</option>
                      )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="common.accountType" /></label>
                  <select
                    value={basicInfo.accountType}
                    onChange={(e) => setBasicInfo({ ...basicInfo, accountType: e.target.value })}
                    disabled={lookupsLoading && accountTypeOptions.length === 0}
                    className="h-10 w-full rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white"
                  >
                    {lookupsLoading && accountTypeOptions.length === 0 ? (
                      <option value="">{i18n.t("crm.loadingAccountTypes")}</option>
                    ) : (
                      accountTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.accountStatus" /></label>
                  <select
                    value={basicInfo.accountStatus}
                    onChange={(e) => setBasicInfo({ ...basicInfo, accountStatus: e.target.value })}
                    disabled={lookupsLoading && accountStatusOptions.length === 0}
                    className="h-10 w-full rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white"
                  >
                    {lookupsLoading && accountStatusOptions.length === 0 ? (
                      <option value="">{i18n.t("crm.loadingAccountStatuses")}</option>
                    ) : (
                      accountStatusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: TAX & LEGAL */}
          {onboardingStep === "tax-legal" && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.taxLegalDetails" /></h3>
              {lookupsError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  {lookupsError}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.legalName" /></label>
                  <input
                    type="text"
                    placeholder={i18n.t("crm.registeredLegalEntityName")}
                    value={taxLegal.legalName}
                    onChange={(e) => setTaxLegal({ ...taxLegal, legalName: e.target.value })}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.entityType" /></label>
                  <select
                    value={taxLegal.entityType}
                    onChange={(e) => setTaxLegal({ ...taxLegal, entityType: e.target.value })}
                    disabled={lookupsLoading && entityTypeOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && entityTypeOptions.length === 0
                        ? i18n.t("crm.loadingEntityTypes")
                        : entityTypeOptions.length === 0
                          ? i18n.t("crm.noEntityTypesAvailable")
                          : i18n.t("crm.selectEntityType")}
                    </option>
                    {entityTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {taxLegal.entityType && !entityTypeOptions.some((o) => o.value === taxLegal.entityType) && (
                      <option value={taxLegal.entityType}>{taxLegal.entityType}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.gstin" /></label>
                  <input
                    type="text"
                    maxLength={15}
                    placeholder={i18n.t("examples.eG22aaaaa0000a1z5")}
                    value={taxLegal.gstin}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      const next = { ...taxLegal, gstin: val };
                      if (val.length >= 12 && !(taxLegal.pan || "").trim()) {
                        next.pan = val.substring(2, 12);
                      }
                      setTaxLegal(next);
                    }}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.pan" /></label>
                  <input
                    type="text"
                    maxLength={10}
                    placeholder={i18n.t("examples.eGAbcde1234f")}
                    value={taxLegal.pan}
                    onChange={(e) => setTaxLegal({ ...taxLegal, pan: e.target.value.toUpperCase() })}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.gstRegistrationType" /></label>
                  <select
                    value={taxLegal.gstRegistrationType}
                    onChange={(e) => setTaxLegal({ ...taxLegal, gstRegistrationType: e.target.value })}
                    disabled={lookupsLoading && gstRegistrationTypeOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && gstRegistrationTypeOptions.length === 0
                        ? i18n.t("crm.loadingGstRegistrationTypes")
                        : gstRegistrationTypeOptions.length === 0
                          ? i18n.t("crm.noGstRegistrationTypesAvailable")
                          : i18n.t("crm.selectGstRegistrationType")}
                    </option>
                    {gstRegistrationTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {taxLegal.gstRegistrationType &&
                      !gstRegistrationTypeOptions.some((o) => o.value === taxLegal.gstRegistrationType) && (
                      <option value={taxLegal.gstRegistrationType}>{taxLegal.gstRegistrationType}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5 justify-end">
                  <label className="inline-flex items-center gap-2 h-10 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={Boolean(taxLegal.isTdsDeductor)}
                      onChange={(e) => setTaxLegal({ ...taxLegal, isTdsDeductor: e.target.checked })}
                      className="h-4 w-4 rounded border-[var(--color-text-border)] text-[var(--color-primary)] focus:ring-[var(--color-accent)]"
                    />
                    <span className="text-sm font-medium text-foreground"><Trans i18nKey="crm.tdsDeductor" /></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: COMMERCIAL TERMS */}
          {onboardingStep === "commercial-terms" && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.commercialPaymentTerms" /></h3>
              {lookupsError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  {lookupsError}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.paymentTerms" /></label>
                  <select
                    value={commercialTerms.paymentTerms}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, paymentTerms: e.target.value })}
                    disabled={lookupsLoading && paymentTermsOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && paymentTermsOptions.length === 0
                        ? i18n.t("crm.loadingPaymentTerms")
                        : paymentTermsOptions.length === 0
                          ? i18n.t("crm.noPaymentTermsAvailable")
                          : i18n.t("crm.selectPaymentTerms")}
                    </option>
                    {paymentTermsOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {commercialTerms.paymentTerms &&
                      !paymentTermsOptions.some((o) => o.value === commercialTerms.paymentTerms) && (
                      <option value={commercialTerms.paymentTerms}>{commercialTerms.paymentTerms}</option>
                    )}
                  </select>
                </div>

                {commercialTerms.paymentTerms === "Custom" && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="dateTime.customCreditDays" /></label>
                    <input
                      type="number"
                      min="1"
                      placeholder={i18n.t("examples.eG40")}
                      value={commercialTerms.customDays}
                      onChange={(e) => setCommercialTerms({ ...commercialTerms, customDays: e.target.value })}
                      className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                    />
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.creditLimit" /></label>
                  <input
                    type="number"
                    placeholder={i18n.t("examples.eG50000")}
                    value={commercialTerms.creditLimit}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, creditLimit: e.target.value })}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.retentionPercentage" /></label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder={i18n.t("examples.eG5")}
                    value={commercialTerms.retentionPercentage}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, retentionPercentage: e.target.value })}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="dateTime.retentionRelease" /></label>
                  <select
                    value={commercialTerms.retentionRelease}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, retentionRelease: e.target.value })}
                    disabled={lookupsLoading && retentionReleaseOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && retentionReleaseOptions.length === 0
                        ? i18n.t("crm.loadingRetentionRelease")
                        : retentionReleaseOptions.length === 0
                          ? i18n.t("crm.noRetentionReleaseOptions")
                          : i18n.t("crm.selectRetentionRelease")}
                    </option>
                    {retentionReleaseOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {commercialTerms.retentionRelease &&
                      !retentionReleaseOptions.some((o) => o.value === commercialTerms.retentionRelease) && (
                      <option value={commercialTerms.retentionRelease}>{commercialTerms.retentionRelease}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.securityDepositAmount" /></label>
                  <input
                    type="number"
                    placeholder={i18n.t("examples.eG10000")}
                    value={commercialTerms.securityDepositAmount}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, securityDepositAmount: e.target.value })}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.billingCycle" /></label>
                  <select
                    value={commercialTerms.billingCycle}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, billingCycle: e.target.value })}
                    disabled={lookupsLoading && billingCycleOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && billingCycleOptions.length === 0
                        ? i18n.t("crm.loadingBillingCycles")
                        : billingCycleOptions.length === 0
                          ? i18n.t("crm.noBillingCyclesAvailable")
                          : i18n.t("crm.selectBillingCycle")}
                    </option>
                    {billingCycleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {commercialTerms.billingCycle &&
                      !billingCycleOptions.some((o) => o.value === commercialTerms.billingCycle) && (
                      <option value={commercialTerms.billingCycle}>{commercialTerms.billingCycle}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.preferredPaymentMode" /></label>
                  <select
                    value={commercialTerms.preferredPaymentMode}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, preferredPaymentMode: e.target.value })}
                    disabled={lookupsLoading && preferredPaymentModeOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && preferredPaymentModeOptions.length === 0
                        ? i18n.t("crm.loadingPaymentModes")
                        : preferredPaymentModeOptions.length === 0
                          ? i18n.t("crm.noPaymentModesAvailable")
                          : i18n.t("crm.selectPaymentMode")}
                    </option>
                    {preferredPaymentModeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {commercialTerms.preferredPaymentMode &&
                      !preferredPaymentModeOptions.some((o) => o.value === commercialTerms.preferredPaymentMode) && (
                      <option value={commercialTerms.preferredPaymentMode}>{commercialTerms.preferredPaymentMode}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.rateCardTier" /></label>
                  <select
                    value={commercialTerms.rateCardTier}
                    onChange={(e) => setCommercialTerms({ ...commercialTerms, rateCardTier: e.target.value })}
                    disabled={lookupsLoading && rateCardTierOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && rateCardTierOptions.length === 0
                        ? i18n.t("crm.loadingRateCardTiers")
                        : rateCardTierOptions.length === 0
                          ? i18n.t("crm.noRateCardTiersAvailable")
                          : i18n.t("crm.selectRateCardTier")}
                    </option>
                    {rateCardTierOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {commercialTerms.rateCardTier &&
                      !rateCardTierOptions.some((o) => o.value === commercialTerms.rateCardTier) && (
                      <option value={commercialTerms.rateCardTier}>{commercialTerms.rateCardTier}</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: RELATIONSHIP */}
          {onboardingStep === "relationship" && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-foreground"><Trans i18nKey="crm.relationshipProfile" /></h3>
              {lookupsError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  {lookupsError}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.leadSource" /></label>
                  <select
                    value={relationship.leadSource}
                    onChange={(e) => setRelationship({ ...relationship, leadSource: e.target.value })}
                    disabled={lookupsLoading && leadSourceOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && leadSourceOptions.length === 0
                        ? i18n.t("crm.loadingLeadSources")
                        : leadSourceOptions.length === 0
                          ? i18n.t("crm.noLeadSourcesAvailable")
                          : i18n.t("crm.selectLeadSource")}
                    </option>
                    {leadSourceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {relationship.leadSource && !leadSourceOptions.some((o) => o.value === relationship.leadSource) && (
                      <option value={relationship.leadSource}>{relationship.leadSource}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.creditRiskRating" /></label>
                  <select
                    value={relationship.creditRiskRating}
                    onChange={(e) => setRelationship({ ...relationship, creditRiskRating: e.target.value })}
                    disabled={lookupsLoading && creditRiskRatingOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && creditRiskRatingOptions.length === 0
                        ? i18n.t("crm.loadingRiskRatings")
                        : creditRiskRatingOptions.length === 0
                          ? i18n.t("crm.noRiskRatingsAvailable")
                          : i18n.t("crm.selectCreditRiskRating")}
                    </option>
                    {creditRiskRatingOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {relationship.creditRiskRating &&
                      !creditRiskRatingOptions.some((o) => o.value === relationship.creditRiskRating) && (
                      <option value={relationship.creditRiskRating}>{relationship.creditRiskRating}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-[var(--color-text-secondary)] uppercase"><Trans i18nKey="crm.preferredContactChannel" /></label>
                  <select
                    value={relationship.preferredContactChannel}
                    onChange={(e) => setRelationship({ ...relationship, preferredContactChannel: e.target.value })}
                    disabled={lookupsLoading && preferredContactChannelOptions.length === 0}
                    className="h-10 rounded-xl border border-[var(--color-text-border)] px-3 text-sm focus:border-[var(--color-accent)] focus:outline-none bg-white disabled:opacity-60"
                  >
                    <option value="">
                      {lookupsLoading && preferredContactChannelOptions.length === 0
                        ? i18n.t("crm.loadingContactChannels")
                        : preferredContactChannelOptions.length === 0
                          ? i18n.t("crm.noContactChannelsAvailable")
                          : i18n.t("crm.selectPreferredChannel")}
                    </option>
                    {preferredContactChannelOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {relationship.preferredContactChannel &&
                      !preferredContactChannelOptions.some((o) => o.value === relationship.preferredContactChannel) && (
                      <option value={relationship.preferredContactChannel}>{relationship.preferredContactChannel}</option>
                    )}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5 justify-end">
                  <label className="inline-flex items-center gap-2 h-10 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={Boolean(relationship.creditHold)}
                      onChange={(e) => setRelationship({ ...relationship, creditHold: e.target.checked })}
                      className="h-4 w-4 rounded border-[var(--color-text-border)] text-[var(--color-primary)] focus:ring-[var(--color-accent)]"
                    />
                    <span className="text-sm font-medium text-foreground"><Trans i18nKey="crm.creditHold" /></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: CONTACTS */}
          {onboardingStep === "contacts" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-base font-bold text-foreground">{i18n.t("crm.associatedContacts")}{contactCount})</h3>
                  <p className="text-xs text-[var(--color-text-secondary)]"><Trans i18nKey="crm.managePrimaryContactsTechnicalRepresentativesAndNotifi" /></p>
                </div>
                <Button
                  type="button"
                  onClick={addContact}
                  className="h-9 px-3 rounded-xl bg-[var(--color-primary)] text-white text-xs hover:bg-[var(--color-accent)]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <Trans i18nKey="formActions.addContact" />
                </Button>
              </div>

              {contacts.map((contact, idx) => {
                const currentTypeLabel = formatContactTypeLabel(contact.contactType || contact.contact_type);

                return (
                  <div key={idx} className="p-4 border border-[var(--color-text-border)]/60 rounded-xl space-y-4 bg-[var(--color-workspace)]/10">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[var(--color-primary)] uppercase">{i18n.t("common.contact")}{idx + 1}</span>
                        {contact.isPrimary && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-700">
                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                            <Trans i18nKey="crm.primaryContact" />
                          </span>
                        )}
                      </div>
                      {contacts.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeContact(idx)}
                          className="h-7 w-7 p-0 text-[var(--color-error)] hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.firstName" /></label>
                        <input
                          type="text"
                          required
                          value={contact.firstName || contact.first_name || ""}
                          onChange={(e) => updateContact(idx, "firstName", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.lastName" /></label>
                        <input
                          type="text"
                          required
                          value={contact.lastName || contact.last_name || ""}
                          onChange={(e) => updateContact(idx, "lastName", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="common.email" /></label>
                        <input
                          type="email"
                          value={contact.email || ""}
                          onChange={(e) => updateContact(idx, "email", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="common.phone" /></label>
                        {/* <input
                          type="text"
                          value={contact.phone || ""}
                          onChange={(e) => updateContact(idx, "phone", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        /> */}
                        <input
                          type="text"
                          value={contact.phone || ""}
                          onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "").slice(0, 15);
                          updateContact(idx, "phone", value);
                        }}
                          maxLength={15}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.jobTitle" /></label>
                        <input
                          type="text"
                          value={contact.jobTitle || contact.job_title || ""}
                          onChange={(e) => updateContact(idx, "jobTitle", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.contactType" /></label>
                        <select
                          value={currentTypeLabel}
                          onChange={(e) => updateContact(idx, "contactType", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none cursor-pointer"
                        >
                          <option value="Primary">{translateContactTypeDisplay("Primary")}</option>
                          <option value="Secondary">{translateContactTypeDisplay("Secondary")}</option>
                          <option value="Technical">{translateContactTypeDisplay("Technical")}</option>
                          <option value="Emergency">{translateContactTypeDisplay("Emergency")}</option>
                          <option value="General">{translateContactTypeDisplay("General")}</option>
                        </select>
                      </div>
                    </div>

                    {/* Interactive Toggles for Primary & Notifications */}
                    <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-[var(--color-text-border)]/40 text-xs">
                      <label className="flex items-center gap-2 cursor-pointer select-none font-medium text-foreground">
                        <input
                          type="checkbox"
                          checked={Boolean(contact.isPrimary)}
                          onChange={() => handleTogglePrimary(idx, contact)}
                          className="rounded border-[var(--color-text-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                        />
                        <Star className="h-3.5 w-3.5 text-amber-500" />
                        <span><Trans i18nKey="crm.setAsPrimaryContact" /></span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer select-none font-medium text-foreground">
                        <input
                          type="checkbox"
                          checked={Boolean(contact.receivesNotifications)}
                          onChange={() => handleToggleNotifications(idx, contact)}
                          className="rounded border-[var(--color-text-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                        />
                        <Bell className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                        <span><Trans i18nKey="crm.receiveNotifications" /></span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 6: LOCATIONS */}
          {onboardingStep === "locations" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-bold text-foreground">{i18n.t("crm.facilityLocations")}{locationCount})</h3>
                <Button
                  type="button"
                  onClick={addLocation}
                  className="h-9 px-3 rounded-xl bg-[var(--color-primary)] text-white text-xs hover:bg-[var(--color-accent)]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <Trans i18nKey="formActions.addLocation" />
                </Button>
              </div>

              {locations.map((loc, idx) => {
                const isActive = toBooleanFlag(loc.isActive ?? loc.is_active, true);
                const isBilling = extractBillingAddressFlag(loc, false);
                return (
                  <div key={loc.locationId || loc.id || `new-${idx}`} className="p-4 border border-[var(--color-text-border)]/60 rounded-xl space-y-3 bg-[var(--color-workspace)]/10">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-[var(--color-primary)] uppercase">
                        {i18n.t("common.location")}{idx + 1}
                        {(loc.locationId || loc.id) ? `${i18n.t("crm.idPrefix")}${loc.locationId || loc.id}` : i18n.t("crm.newSuffix")}
                      </span>
                      {(locations.length > 1 || loc.locationId || loc.id) && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeLocation(idx)}
                          className="h-7 w-7 p-0 text-[var(--color-error)] hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.locationName" /></label>
                        <input
                          type="text"
                          required
                          placeholder={i18n.t("crm.locationName2")}
                          value={loc.locationName || loc.location_name || ""}
                          onChange={(e) => updateLocation(idx, "locationName", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.siteType" /></label>
                        <select
                          value={loc.siteType || loc.site_type || ""}
                          onChange={(e) => updateLocation(idx, "siteType", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none"
                        >
                          <option value="">{i18n.t("crm.selectSiteType")}</option>
                          {SITE_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>{translateSiteTypeLabel(type)}</option>
                          ))}
                          {loc.siteType && !SITE_TYPE_OPTIONS.includes(loc.siteType) && (
                            <option value={loc.siteType}>{loc.siteType}</option>
                          )}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="common.addressLine1" /></label>
                        <input
                          type="text"
                          required
                          value={loc.addressLine1 || loc.address_line1 || ""}
                          onChange={(e) => updateLocation(idx, "addressLine1", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="common.addressLine2" /></label>
                        <input
                          type="text"
                          value={loc.addressLine2 || loc.address_line2 || ""}
                          onChange={(e) => updateLocation(idx, "addressLine2", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.city" /></label>
                        <input
                          type="text"
                          required
                          value={loc.city || ""}
                          onChange={(e) => updateLocation(idx, "city", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.state" /></label>
                        <select
                          value={loc.state || ""}
                          onChange={(e) => updateLocation(idx, "state", e.target.value)}
                          disabled={lookupsLoading && stateOptions.length === 0}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none disabled:opacity-60"
                        >
                          <option value="">
                            {lookupsLoading && stateOptions.length === 0 ? i18n.t("crm.loadingStates") : i18n.t("crm.selectState")}
                          </option>
                          {stateOptions.map((opt) => (
                            <option key={opt.value} value={opt.label}>{opt.label}</option>
                          ))}
                          {loc.state && !stateOptions.some((o) => o.label === loc.state || o.value === loc.state) && (
                            <option value={loc.state}>{loc.state}</option>
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 pt-1">
                      <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={Boolean(isActive)}
                          onChange={() => handleToggleLocationActive(idx, loc)}
                          className="rounded border-[var(--color-text-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                        />
                        <span><Trans i18nKey="status.active" /></span>
                      </label>
                      <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={Boolean(isBilling)}
                          onChange={(e) => updateLocation(idx, "isBillingAddress", e.target.checked)}
                          className="rounded border-[var(--color-text-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                        />
                        <span><Trans i18nKey="crm.billingAddress" /></span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 7: ASSETS */}
          {onboardingStep === "assets" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-bold text-foreground">{i18n.t("crm.installedAssets")}{assetCount})</h3>
                <Button
                  type="button"
                  onClick={addAsset}
                  disabled={!locations.some((l) => l.locationId || l.id)}
                  className="h-9 px-3 rounded-xl bg-[var(--color-primary)] text-white text-xs hover:bg-[var(--color-accent)] disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <Trans i18nKey="formActions.addAsset" />
                </Button>
              </div>

              {!locations.some((l) => l.locationId || l.id) && (
                <p className="text-xs text-[var(--color-error)]">
                  <Trans i18nKey="formActions.saveAtLeastOneLocationBeforeAddingAssets" />
                </p>
              )}

              {lookupsError && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  {lookupsError}
                </p>
              )}

              {assets.map((asset, idx) => {
                const assetId = asset.assetId || asset.id;
                const locationId = String(asset.locationId || asset.location_id || "");
                return (
                  <div key={assetId || `new-asset-${idx}`} className="p-4 border border-[var(--color-text-border)]/60 rounded-xl space-y-3 bg-[var(--color-workspace)]/10">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-[var(--color-primary)] uppercase">
                        {i18n.t("crm.asset")}{idx + 1}
                        {assetId ? `${i18n.t("crm.idPrefix")}${assetId}` : i18n.t("crm.newSuffix")}
                      </span>
                      {(assets.length > 1 || assetId) && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeAsset(idx)}
                          className="h-7 w-7 p-0 text-[var(--color-error)] hover:bg-rose-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="common.location2" /></label>
                        <select
                          required
                          value={locationId}
                          onChange={(e) => {
                            const nextLocId = e.target.value;
                            if (assetId) {
                              handleRelocateAsset(idx, asset, nextLocId);
                            } else {
                              updateAsset(idx, "locationId", nextLocId);
                            }
                          }}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none"
                        >
                          <option value="">{i18n.t("common.selectLocation")}</option>
                          {locations
                            .filter((l) => l.locationId || l.id)
                            .map((l) => {
                              const locId = l.locationId || l.id;
                              return (
                                <option key={locId} value={String(locId)}>
                                  {l.locationName || l.location_name || `${i18n.t("common.location4")} ${locId}`}
                                </option>
                              );
                            })}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.assetName" /></label>
                        <input
                          type="text"
                          required
                          value={asset.assetName || asset.asset_name || ""}
                          onChange={(e) => updateAsset(idx, "assetName", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="common.category2" /></label>
                        <select
                          required
                          value={asset.assetCategory || asset.asset_category || ""}
                          onChange={(e) => updateAsset(idx, "assetCategory", e.target.value)}
                          disabled={lookupsLoading && categoryOptions.length === 0}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none disabled:opacity-60"
                        >
                          <option value="">
                            {lookupsLoading && categoryOptions.length === 0 ? i18n.t("crm.loadingCategories") : i18n.t("onboarding.selectCategory")}
                          </option>
                          {categoryOptions.map((opt) => (
                            <option key={`${opt.id}-${opt.label}`} value={opt.label}>{opt.label}</option>
                          ))}
                          {(asset.assetCategory || asset.asset_category) &&
                            !categoryOptions.some((o) => o.label === (asset.assetCategory || asset.asset_category)) && (
                            <option value={asset.assetCategory || asset.asset_category}>
                              {asset.assetCategory || asset.asset_category}
                            </option>
                          )}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.manufacturer" /></label>
                        <input
                          type="text"
                          value={asset.manufacturer || ""}
                          onChange={(e) => updateAsset(idx, "manufacturer", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.modelNumber" /></label>
                        <input
                          type="text"
                          value={asset.modelNumber || asset.model_number || ""}
                          onChange={(e) => updateAsset(idx, "modelNumber", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.serialNumber" /></label>
                        <input
                          type="text"
                          value={asset.serialNumber || asset.serial_number || ""}
                          onChange={(e) => updateAsset(idx, "serialNumber", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.installDate" /></label>
                        <input
                          type="date"
                          value={asset.installDate || asset.install_date || ""}
                          onChange={(e) => updateAsset(idx, "installDate", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.warrantyType" /></label>
                        <select
                          value={
                            (() => {
                              const raw = asset.warrantyType || asset.warranty_type || "";
                              return WARRANTY_TYPE_OPTIONS.includes(raw) ? raw : "";
                            })()
                          }
                          onChange={(e) => updateAsset(idx, "warrantyType", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none"
                        >
                          <option value="">{i18n.t("crm.selectWarrantyType")}</option>
                          {WARRANTY_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>{translateWarrantyTypeLabel(type)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.warrantyStartDate" /></label>
                        <input
                          type="date"
                          value={asset.warrantyStartDate || asset.warranty_start_date || ""}
                          onChange={(e) => updateAsset(idx, "warrantyStartDate", e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.warrantyEndDate" /></label>
                        <input
                          type="date"
                          value={
                            asset.warrantyExpiry ||
                            asset.warranty_expiry ||
                            asset.warrantyEndDate ||
                            asset.warranty_end_date ||
                            ""
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            setAssets((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? {
                                      ...row,
                                      warrantyExpiry: val,
                                      warrantyEndDate: val,
                                      warranty_expiry: val,
                                      warranty_end_date: val
                                    }
                                  : row
                              )
                            );
                          }}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.amcType" /></label>
                        <select
                          value={asset.amcType || asset.amc_type || asset.AMCType || ""}
                          onChange={(e) => handleAmcTypeChange(idx, e.target.value)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none"
                        >
                          <option value="">{i18n.t("crm.selectAmcType")}</option>
                          {AMC_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>{translateAmcTypeLabel(type)}</option>
                          ))}
                          {(asset.amcType || asset.amc_type || asset.AMCType) &&
                            !AMC_TYPE_OPTIONS.includes(asset.amcType || asset.amc_type || asset.AMCType) && (
                              <option value={asset.amcType || asset.amc_type || asset.AMCType}>
                                {asset.amcType || asset.amc_type || asset.AMCType}
                              </option>
                            )}
                        </select>
                      </div>
                      {!isNoAmcType(asset) && (
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.amcOrder" /></label>
                          <input
                            type="text"
                            value={asset.amcOrder || asset.amc_order || asset.AMCOrder || asset.amcOrderNo || asset.amc_order_no || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAssets((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        amcOrder: val,
                                        amc_order: val,
                                        AMCOrder: val,
                                        amcOrderNo: val,
                                        amc_order_no: val
                                      }
                                    : row
                                )
                              );
                            }}
                            className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                          />
                        </div>
                      )}
                      {!isNoAmcType(asset) && (
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.amcOrderDate" /></label>
                          <input
                            type="date"
                            value={asset.amcOrderDate || asset.amc_order_date || asset.AMCOrderDate || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAssets((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        amcOrderDate: val,
                                        amc_order_date: val,
                                        AMCOrderDate: val
                                      }
                                    : row
                                )
                              );
                            }}
                            className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.tenantAssetId" /></label>
                        <input
                          type="text"
                          value={asset.tenantAssetId || asset.tenant_asset_id || asset.TenantAssetId || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAssets((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? {
                                      ...row,
                                      tenantAssetId: val,
                                      tenant_asset_id: val,
                                      TenantAssetId: val
                                    }
                                  : row
                              )
                            );
                          }}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        />
                      </div>
                      {!isNoAmcType(asset) && (
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.amcStartDate" /></label>
                          <input
                            type="date"
                            value={
                              asset.AMCStartDate ||
                              asset.amcStartDate ||
                              asset.amc_start_date ||
                              asset.AMC_Start_Date ||
                              ""
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setAssets((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        AMCStartDate: val,
                                        amcStartDate: val,
                                        amc_start_date: val,
                                        AMC_Start_Date: val
                                      }
                                    : row
                                )
                              );
                            }}
                            className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                          />
                        </div>
                      )}
                      {!isNoAmcType(asset) && (
                        <div className="flex flex-col gap-1">
                          <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="dateTime.amcEndDate" /></label>
                          <input
                            type="date"
                            value={
                              asset.AMCEndDate ||
                              asset.amcEndDate ||
                              asset.amc_end_date ||
                              asset.AMC_End_Date ||
                              ""
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setAssets((prev) =>
                                prev.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        AMCEndDate: val,
                                        amcEndDate: val,
                                        amc_end_date: val,
                                        AMC_End_Date: val
                                      }
                                    : row
                                )
                              );
                            }}
                            className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <label className="text-[11px] font-bold text-[var(--color-text-secondary)]"><Trans i18nKey="crm.assetStatus" /></label>
                        <select
                          value={
                            (() => {
                              const raw = asset.assetStatus || asset.asset_status || "Active";
                              if (String(raw).toLowerCase() === "inactive" || raw === "InActive") return "Inactive";
                              if (ASSET_STATUS_OPTIONS.includes(raw)) return raw;
                              return "Active";
                            })()
                          }
                          onChange={(e) => {
                            const next = e.target.value;
                            updateAsset(idx, "assetStatus", next);
                            if (assetId) handleAssetStatusChange(idx, asset, next);
                          }}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2 text-xs bg-white focus:outline-none"
                        >
                          {ASSET_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{translateAssetStatusLabel(status)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        {/* <label className="text-[11px] font-bold text-[var(--color-text-secondary)]">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={extractAssetQuantity(asset) ?? asset.quantity ?? 1}
                          onChange={(e) => updateAsset(idx, "quantity", parseInt(e.target.value, 10) || 1)}
                          className="h-9 rounded-lg border border-[var(--color-text-border)] px-2.5 text-xs bg-white focus:outline-none"
                        /> */}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Form Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-text-border)]/50">
            <Button
              type="button"
              onClick={navigateBack}
              className="h-10 px-4 rounded-xl border border-[var(--color-text-border)] bg-white hover:bg-[var(--color-workspace)] text-foreground text-sm font-semibold"
            >
              <Trans i18nKey="formActions.cancel" />
            </Button>
            <Button
              type="submit"
              disabled={loading || isSubmittingStep}
              className="h-10 px-6 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] text-sm font-semibold shadow-[0_4px_12px_rgba(30,51,131,0.25)] cursor-pointer disabled:opacity-50"
            >
              {loading ? i18n.t("inventory.saving") : onboardingStep === "basic-info" ? i18n.t("crm.saveAndContinue") : onboardingStep === "assets" ? i18n.t("crm.finishOnboarding") : i18n.t("crm.saveAndContinue")}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
